import { useCallback, useEffect, useState } from 'react'
import { useEffectEvent } from 'use-effect-event'

import type { AiEncryptionKey } from '@/src/domains/ai/types'

import { useAiSetupProviderEncryptionKey } from './useAiSetupProviderEncryptionKey'
import { useAiSetupProviderRecoveryKey } from './useAiSetupProviderRecoveryKey'

export const useAiSetupProvider = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSetupDeclined, setIsSetupDeclined] = useState(false)

  const { encryptionKey, hasEncryptionKey, requestEncryptionKey, submitEncryptionKey, settlePendingEncryptionKey } =
    useAiSetupProviderEncryptionKey()
  const { recoveryKey, holdRecoveryKey, persistRecoveryKey, removeRecoveryKey } = useAiSetupProviderRecoveryKey()

  const handleDialogClose = useEffectEvent(() => {
    if (recoveryKey) {
      persistRecoveryKey(recoveryKey)
    }
  })

  useEffect(() => {
    if (!isDialogOpen) {
      handleDialogClose()
    }

    // effect events shouldn't be included in the dependency array, as they are guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen])

  const openSetupDialog = useCallback(() => {
    setIsDialogOpen(true)
  }, [])

  const getEncryptionKey = useCallback(() => {
    if (!hasEncryptionKey()) {
      setIsDialogOpen(true)
    }

    return requestEncryptionKey()
  }, [hasEncryptionKey, requestEncryptionKey])

  const handleEncryptionKeySubmit = useCallback(
    (key: AiEncryptionKey, createdRecoveryKey?: string) => {
      submitEncryptionKey(key)
      if (createdRecoveryKey) {
        holdRecoveryKey(createdRecoveryKey)
      }
      setIsSetupDeclined(false)
    },
    [submitEncryptionKey, holdRecoveryKey],
  )

  const handleRecoveryKeySubmit = useCallback(() => {
    removeRecoveryKey()
  }, [removeRecoveryKey])

  const handleDialogOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        return
      }

      if (!hasEncryptionKey()) {
        setIsSetupDeclined(true)
      }

      settlePendingEncryptionKey()
      setIsDialogOpen(false)
    },
    [hasEncryptionKey, settlePendingEncryptionKey],
  )

  return {
    encryptionKey,
    recoveryKey,
    isSetupDeclined,
    isDialogOpen,
    getEncryptionKey,
    openSetupDialog,
    handleEncryptionKeySubmit,
    handleRecoveryKeySubmit,
    handleDialogOpenChange,
  }
}
